import React, { useState } from 'react';
import {
  Check,
  X,
  Info,
  Layers,
  Wrench,
  ShieldCheck,
  Calculator,
  Compass,
  Cpu,
  Sparkles,
  ShoppingBag
} from 'lucide-react';

const ENGINEER_META = {
  cost: {
    id: 'cost',
    name: 'Costing',
    tone: 'blue',
    icon: Calculator,
    bg: 'var(--blue-tint)',
    fg: 'var(--blue)',
    hire: [1200, 18000],
    rent: [300, 4500]
  },
  dfm: {
    id: 'dfm',
    name: 'DFM',
    tone: 'teal',
    icon: ShieldCheck,
    bg: 'var(--green-tint)',
    fg: 'var(--green-ink)',
    hire: [1200, 18000],
    rent: [300, 4500]
  },
  rnd: {
    id: 'rnd',
    name: 'R&D',
    tone: 'purple',
    icon: Sparkles,
    bg: 'var(--purple-tint)',
    fg: 'var(--purple)',
    hire: [1400, 21000],
    rent: [350, 5250]
  },
  design: {
    id: 'design',
    name: 'Design',
    tone: 'blue',
    icon: Compass,
    bg: 'var(--blue-tint)',
    fg: 'var(--blue)',
    hire: [1800, 27000],
    rent: [450, 6750]
  },
  sim: {
    id: 'sim',
    name: 'Simulation',
    tone: 'indigo',
    icon: Cpu,
    bg: 'var(--indigo-tint)',
    fg: 'var(--indigo)',
    hire: [1600, 24000],
    rent: [400, 6000]
  },
  mfg: {
    id: 'mfg',
    name: 'Manufacturing',
    tone: 'slate',
    icon: Wrench,
    bg: 'var(--slate-tint)',
    fg: 'var(--slate)',
    hire: [1200, 18000],
    rent: [300, 4500]
  },
  qa: {
    id: 'qa',
    name: 'Quality',
    tone: 'rose',
    icon: Layers,
    bg: 'var(--rose-tint)',
    fg: 'var(--rose)',
    hire: [1000, 15000],
    rent: [250, 3750]
  },
  proc: {
    id: 'proc',
    name: 'Procurement',
    tone: 'amber',
    icon: ShoppingBag,
    bg: 'var(--amber-tint)',
    fg: 'var(--amber)',
    hire: [1100, 16500],
    rent: [280, 4200]
  }
};

export default function EngineerHireModal({ engineerId, mode = 'hire', isOpen, onClose, onSuccess }) {
  const [isSuccess, setIsSuccess] = useState(false);

  if (!isOpen || !engineerId) return null;

  const eng = ENGINEER_META[engineerId] || ENGINEER_META.rnd;
  const isHire = mode === 'hire';
  const pricing = isHire ? eng.hire : eng.rent;
  const [credits, inr] = pricing;
  const IconComponent = eng.icon;

  const handleConfirm = () => {
    setIsSuccess(true);
    setTimeout(() => {
      setIsSuccess(false);
      onSuccess?.(engineerId, mode);
      onClose();
    }, 1500);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 bg-[#102442]/45 backdrop-blur-xs z-50 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-[430px] max-w-full overflow-hidden border border-slate-200 animate-in fade-in zoom-in-95 duration-150">
        {isSuccess ? (
          <div className="text-center py-10 px-6">
            <div className="w-14 h-14 rounded-full bg-[#E4F5EF] text-[#0C8A74] flex items-center justify-center mx-auto mb-4 shadow-xs">
              <Check className="w-7 h-7 stroke-[2.5]" />
            </div>
            <h3 className="text-base font-semibold text-slate-900">
              {eng.name} engineer {isHire ? 'hired' : 'rented'}
            </h3>
            <p className="text-xs text-slate-500 mt-2 max-w-[34ch] mx-auto leading-relaxed">
              {isHire
                ? 'Added to your team and ready to staff on your next problem.'
                : 'Available for your next project.'}
            </p>
          </div>
        ) : (
          <>
            {/* Modal Header */}
            <div className="flex items-start gap-3.5 p-5 pb-3.5 border-b border-slate-100">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                style={{ backgroundColor: eng.bg, color: eng.fg }}
              >
                <IconComponent className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-slate-900 leading-tight">
                  {isHire ? 'Hire' : 'Rent'} {eng.name} engineer
                </h3>
                <p className="text-xs text-slate-500 mt-1 leading-snug">
                  {isHire
                    ? 'Add this engineer to your team — unlimited use, one accumulating knowledge base.'
                    : 'Bring this engineer in for a single project or a short window.'}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="text-slate-400 hover:text-slate-700 p-1 rounded-lg hover:bg-slate-100 transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 pt-4 space-y-4">
              <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
                <div className="flex justify-between items-center px-4 py-3 text-xs text-slate-600">
                  <span>Your balance</span>
                  <b className="font-semibold text-slate-900 font-mono">1,840 credits</b>
                </div>
                <div className="flex justify-between items-center px-4 py-3 text-xs text-slate-600 border-t border-slate-100">
                  <span>
                    {eng.name} engineer{' '}
                    <span className="text-[11px] text-slate-400 font-normal">
                      ({isHire ? 'per month' : 'per project'})
                    </span>
                  </span>
                  <b className="font-semibold text-slate-900 font-mono">
                    {credits.toLocaleString('en-IN')} credits
                  </b>
                </div>
                <div className="flex justify-between items-center px-4 py-3 text-xs bg-[#EFF5FE] border-t border-slate-100">
                  <span className="font-medium text-[#0A4FBB]">Pay now</span>
                  <b className="font-bold text-[#0A4FBB] text-sm font-mono">
                    ₹{inr.toLocaleString('en-IN')}
                  </b>
                </div>
              </div>

              <div className="flex items-start gap-2 text-[11.5px] text-slate-400 leading-snug">
                <Info className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
                <span>Sample pricing — this prototype does not charge anything.</span>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2.5 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 py-2.5 px-4 rounded-lg border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  className="flex-1 py-2.5 px-4 rounded-lg bg-[#0C64E8] hover:bg-[#0A4FBB] text-white text-xs font-semibold shadow-xs shadow-blue-500/30 transition-all active:scale-[0.98]"
                >
                  {isHire ? 'Hire engineer' : 'Rent engineer'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
